import React from 'react';
import { MemberRegistrationForm, MembershipType } from '../../types';
import { getMembershipLabel } from '../../utils/membership';
import { registrationAmountDollars } from '../../utils/registrationPrice';
import { formatClubDate } from '../../utils/clubTime';

interface ReviewStepProps {
  formData: MemberRegistrationForm;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting: boolean;
  submitError: string | null;
}

const isSponsorType = (type: MembershipType) =>
  type === MembershipType.SPONSOR_GOLD ||
  type === MembershipType.SPONSOR_SILVER ||
  type === MembershipType.SPONSOR_BRONZE;

const hasFamilyMembers = (type: MembershipType) =>
  type === MembershipType.FAMILY || isSponsorType(type);

const ReviewStep: React.FC<ReviewStepProps> = ({
  formData,
  onSubmit,
  onBack,
  isSubmitting,
  submitError,
}) => {
  const formatDate = (dateString: string) =>
    formatClubDate(dateString, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  const price = registrationAmountDollars(formData);

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Review Your Application</h2>
      <p className="text-gray-600 mb-6">Please review your information before submitting</p>

      <div className="space-y-6">
        {/* Personal Details */}
        <div className="border-b pb-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Personal Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Name:</span>
              <p className="font-medium">
                {formData.firstName} {formData.lastName}
              </p>
            </div>
            <div>
              <span className="text-gray-600">Email:</span>
              <p className="font-medium">{formData.email}</p>
            </div>
            <div>
              <span className="text-gray-600">Date of Birth:</span>
              <p className="font-medium">{formatDate(formData.dateOfBirth)}</p>
            </div>
            <div>
              <span className="text-gray-600">Phone:</span>
              <p className="font-medium">{formData.phoneNumber}</p>
            </div>
            <div>
              <span className="text-gray-600">Gender:</span>
              <p className="font-medium">{formData.gender.replace(/_/g, ' ')}</p>
            </div>
            <div>
              <span className="text-gray-600">Shirt Size:</span>
              <p className="font-medium">{formData.shirtSize}</p>
            </div>
            <div className="md:col-span-2">
              <span className="text-gray-600">Address:</span>
              <p className="font-medium">
                {formData.address}
                {formData.suburb && (
                  <>
                    <br />
                    {formData.suburb}
                    {formData.state ? `, ${formData.state}` : ''} {formData.postCode || ''}
                  </>
                )}
                {!formData.suburb && formData.postCode && ` ${formData.postCode}`}
              </p>
            </div>
            {formData.emergencyContactName && (
              <>
                <div>
                  <span className="text-gray-600">Emergency Contact:</span>
                  <p className="font-medium">{formData.emergencyContactName}</p>
                </div>
                <div>
                  <span className="text-gray-600">Emergency Phone:</span>
                  <p className="font-medium">{formData.emergencyContactPhone}</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Membership Type & Price */}
        <div className="border-b pb-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Membership</h3>
          <div className="text-sm">
            <span className="text-gray-600">Type:</span>
            <p className="font-medium">{getMembershipLabel(formData.membershipType)}</p>
            <p className="text-fishing-green font-semibold text-lg mt-1">
              ${price.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Sponsor Details */}
        {isSponsorType(formData.membershipType) && formData.businessName && (
          <div className="border-b pb-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Sponsor Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Business Name:</span>
                <p className="font-medium">{formData.businessName}</p>
              </div>
              <div>
                <span className="text-gray-600">Sponsor Tier:</span>
                <p className="font-medium">{formData.sponsorTier}</p>
              </div>
            </div>
          </div>
        )}

        {/* Guardian Details (Junior) */}
        {formData.membershipType === MembershipType.JUNIOR && formData.guardianFirstName && (
          <div className="border-b pb-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Guardian Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Guardian Name:</span>
                <p className="font-medium">
                  {formData.guardianFirstName} {formData.guardianLastName}
                </p>
              </div>
              <div>
                <span className="text-gray-600">Guardian Email:</span>
                <p className="font-medium">{formData.guardianEmail}</p>
              </div>
              <div>
                <span className="text-gray-600">Guardian Phone:</span>
                <p className="font-medium">{formData.guardianPhone}</p>
              </div>
            </div>
          </div>
        )}

        {/* Family Members */}
        {hasFamilyMembers(formData.membershipType) && formData.familyMembers.length > 0 && (
          <div className="border-b pb-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Family Members ({formData.familyMembers.length})
            </h3>
            <div className="space-y-3">
              {formData.familyMembers.map((member, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-3 text-sm">
                  <p className="font-medium">
                    {member.isAdult ? '👤' : '🧒'} {member.firstName} {member.lastName}
                  </p>
                  <div className="grid grid-cols-3 gap-2 mt-1 text-gray-600">
                    <span>DOB: {formatDate(member.dateOfBirth)}</span>
                    <span>Gender: {member.gender.replace(/_/g, ' ')}</span>
                    <span>Shirt: {member.shirtSize}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Agreements */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Agreements</h3>
          <div className="space-y-1 text-sm">
            <div className="flex items-center">
              <span className="text-green-600 mr-2">✓</span>
              <span className="text-gray-700">Club Rules accepted</span>
            </div>
            <div className="flex items-center">
              <span className="text-green-600 mr-2">✓</span>
              <span className="text-gray-700">Code of Conduct accepted</span>
            </div>
            <div className="flex items-center">
              <span className="text-green-600 mr-2">✓</span>
              <span className="text-gray-700">Waiver and Release accepted</span>
            </div>
          </div>
        </div>
      </div>

      {/* Approval Notice */}
      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-800 font-medium">
          📋 An admin will review and approve your application.
        </p>
      </div>

      {/* Next Steps */}
      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-semibold text-gray-800 mb-2">What happens next?</h4>
        <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
          <li>Your application will be reviewed by our admin team</li>
          <li>Pay your membership fee online now, or in person on a competition day</li>
          <li>We'll email confirmation and activate your membership once payment is verified</li>
          <li>
            Club merchandise available from time to time, plus full member benefits to the
            best fishing club in Tumbulgum!
          </li>
        </ol>
      </div>

      {submitError && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{submitError}</p>
        </div>
      )}

      <div className="flex justify-between mt-8">
        <button
          onClick={onBack}
          disabled={isSubmitting}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back
        </button>
        <button
          onClick={onSubmit}
          disabled={isSubmitting}
          className="px-8 py-2 bg-fishing-green hover:bg-green-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {isSubmitting ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Submitting...
            </>
          ) : (
            'Submit Application'
          )}
        </button>
      </div>
    </div>
  );
};

export default ReviewStep;
